import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import "../css/shop.scss";
import productData from "../Datas/productData";
import siteConfig from "../Datas/siteConfig";
import { CartContext } from "./CartContext";

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
});

const emptyWishlist = [];

const wholeCurrencyFormatter = new Intl.NumberFormat(siteConfig.locale, {
  style: "currency",
  currency: siteConfig.currency,
  maximumFractionDigits: 0,
});

const priceRanges = [
  {
    value: "under-100",
    label: `Under ${wholeCurrencyFormatter.format(100)}`,
    min: 0,
    max: 100,
  },
  {
    value: "100-200",
    label: `${wholeCurrencyFormatter.format(100)} – ${wholeCurrencyFormatter.format(200)}`,
    min: 100,
    max: 200,
  },
  {
    value: "200-300",
    label: `${wholeCurrencyFormatter.format(200)} – ${wholeCurrencyFormatter.format(300)}`,
    min: 200,
    max: 300,
  },
  {
    value: "300-plus",
    label: `${wholeCurrencyFormatter.format(300)} and over`,
    min: 300,
    max: Infinity,
  },
];

const asArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value === null || value === undefined || value === "") return [];
  return [String(value)];
};

const productValues = (product, field) => {
  if (field === "activity") {
    return asArray(product.activity || product.attributes?.activity);
  }
  return asArray(product[field]);
};

const uniqueValues = (field) =>
  Array.from(
    new Set(productData.flatMap((product) => productValues(product, field)))
  ).sort((a, b) => a.localeCompare(b));

const categoryOptions = uniqueValues("category");
const audienceOptions = uniqueValues("audience");
const activityOptions = uniqueValues("activity");
const sizeOptions = uniqueValues("size");

const slugify = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const resolveUrlValues = (rawValue, options) => {
  if (!rawValue) return [];

  return rawValue
    .split(",")
    .map((value) => value.trim())
    .map((value) =>
      options.find(
        (option) =>
          option.toLowerCase() === value.toLowerCase() ||
          slugify(option) === slugify(value)
      )
    )
    .filter(Boolean);
};

const sameValues = (left, right) =>
  left.length === right.length && left.every((value) => right.includes(value));

const toggleValue = (setter, value) => {
  setter((current) =>
    current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]
  );
};

const colorToCss = (color) => {
  const label = String(color).toLowerCase();
  const knownColors = {
    black: "#171717",
    white: "#f7f6f1",
    grey: "#898b89",
    gray: "#898b89",
    navy: "#15283f",
    blue: "#315e8a",
    green: "#526b45",
    olive: "#66704b",
    red: "#a72b2b",
    orange: "#c85b2b",
    yellow: "#e6bf19",
    beige: "#c9b99e",
    brown: "#745442",
    pink: "#c67a83",
    purple: "#694f70",
  };
  const match = Object.keys(knownColors).find((name) => label.includes(name));
  if (match) return knownColors[match];

  const hue = [...label].reduce((total, character) => {
    return (total + character.charCodeAt(0) * 7) % 360;
  }, 0);
  return `hsl(${hue} 22% 43%)`;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

function FilterGroup({ title, children, initiallyOpen = true }) {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  return (
    <section className="jw-filter-group">
      <button
        type="button"
        className="jw-filter-group__toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>{title}</span>
        <FiChevronDown aria-hidden="true" />
      </button>
      {isOpen && <div className="jw-filter-group__content">{children}</div>}
    </section>
  );
}

function CheckboxList({ idPrefix, options, selected, onChange }) {
  return options.map((option) => {
    const id = `${idPrefix}-${slugify(option)}`;
    return (
      <label className="jw-filter-option" htmlFor={id} key={option}>
        <input
          id={id}
          type="checkbox"
          checked={selected.includes(option)}
          onChange={() => onChange(option)}
        />
        <span className="jw-filter-option__box" aria-hidden="true" />
        <span>{option}</span>
      </label>
    );
  });
}

function FilterPanel({
  idPrefix,
  selectedCategories,
  setSelectedCategories,
  selectedAudiences,
  setSelectedAudiences,
  selectedActivities,
  setSelectedActivities,
  selectedSizes,
  setSelectedSizes,
  priceRange,
  setPriceRange,
  onClear,
  activeFilterCount,
}) {
  return (
    <div className="jw-filters">
      <div className="jw-filters__heading">
        <h2>Filter</h2>
        {activeFilterCount > 0 && (
          <button type="button" onClick={onClear}>
            Clear all
          </button>
        )}
      </div>

      <FilterGroup title="Category">
        <CheckboxList
          idPrefix={`${idPrefix}-category`}
          options={categoryOptions}
          selected={selectedCategories}
          onChange={(value) => toggleValue(setSelectedCategories, value)}
        />
      </FilterGroup>

      <FilterGroup title="Who is it for?">
        <CheckboxList
          idPrefix={`${idPrefix}-audience`}
          options={audienceOptions}
          selected={selectedAudiences}
          onChange={(value) => toggleValue(setSelectedAudiences, value)}
        />
      </FilterGroup>

      <FilterGroup title="Activity">
        <CheckboxList
          idPrefix={`${idPrefix}-activity`}
          options={activityOptions}
          selected={selectedActivities}
          onChange={(value) => toggleValue(setSelectedActivities, value)}
        />
      </FilterGroup>

      <FilterGroup title="Size" initiallyOpen={false}>
        <div className="jw-filter-sizes">
          {sizeOptions.map((size) => {
            const id = `${idPrefix}-size-${slugify(size)}`;
            return (
              <label htmlFor={id} key={size}>
                <input
                  id={id}
                  type="checkbox"
                  checked={selectedSizes.includes(size)}
                  onChange={() => toggleValue(setSelectedSizes, size)}
                />
                <span>{size}</span>
              </label>
            );
          })}
        </div>
      </FilterGroup>

      <FilterGroup title="Price" initiallyOpen={false}>
        {priceRanges.map((range) => {
          const id = `${idPrefix}-price-${range.value}`;
          return (
            <label className="jw-filter-option" htmlFor={id} key={range.value}>
              <input
                id={id}
                type="radio"
                name={`${idPrefix}-price`}
                checked={priceRange === range.value}
                onChange={() => setPriceRange(range.value)}
              />
              <span className="jw-filter-option__radio" aria-hidden="true" />
              <span>{range.label}</span>
            </label>
          );
        })}
        {priceRange && (
          <button
            type="button"
            className="jw-filter-group__clear"
            onClick={() => setPriceRange("")}
          >
            Clear price
          </button>
        )}
      </FilterGroup>
    </div>
  );
}

function ProductCard({ product, isWishlisted, onToggleWishlist }) {
  const primaryImage = product.image || product.images?.[0];
  const secondaryImage = product.images?.find(
    (image) => image && image !== primaryImage
  );
  const colors = asArray(product.colors);
  const price = Number(product.price);
  const compareAtPrice = Number(product.compareAtPrice);

  const handleImageError = (event) => {
    const image = event.currentTarget;
    const media = image.closest(".jw-product-card__media");

    if (
      image.dataset.role === "primary" &&
      secondaryImage &&
      image.dataset.fallbackTried !== "true"
    ) {
      image.dataset.fallbackTried = "true";
      image.src = secondaryImage;
      media?.classList.add("has-no-secondary");
      return;
    }

    image.hidden = true;
    if (image.dataset.role === "secondary") {
      media?.classList.add("has-no-secondary");
    } else {
      media?.classList.add("is-image-missing");
    }
  };

  return (
    <article className="jw-product-card">
      <div
        className={`jw-product-card__media${
          secondaryImage ? "" : " has-no-secondary"
        }`}
      >
        {(Number(product.stock) === 0 || product.badge) && (
          <span className="jw-product-card__badge">
            {Number(product.stock) === 0 ? "Out of stock" : product.badge}
          </span>
        )}
        <button
          className="jw-product-card__wish"
          type="button"
          aria-label={`${isWishlisted ? "Remove" : "Add"} ${product.name} ${
            isWishlisted ? "from" : "to"
          } wishlist`}
          aria-pressed={isWishlisted}
          onClick={() => onToggleWishlist(product.id)}
        >
          <FiHeart aria-hidden="true" />
        </button>
        <Link
          to={`/shopDetails/${product.id}`}
          onClick={scrollToTop}
          aria-label={`View ${product.name}`}
        >
          {primaryImage && (
            <img
              className="jw-product-card__image jw-product-card__image--primary"
              data-role="primary"
              src={primaryImage}
              alt={product.name}
              loading="lazy"
              decoding="async"
              onError={handleImageError}
            />
          )}
          {secondaryImage && (
            <img
              className="jw-product-card__image jw-product-card__image--secondary"
              data-role="secondary"
              src={secondaryImage}
              alt=""
              loading="lazy"
              decoding="async"
              onError={handleImageError}
            />
          )}
        </Link>
      </div>

      <div className="jw-product-card__body">
        <div className="jw-product-card__meta">
          <span>{product.category}</span>
          {product.rating && (
            <span aria-label={`${product.rating} out of 5 stars`}>
              <span aria-hidden="true">★</span> {product.rating}
              {product.reviews ? ` (${product.reviews})` : ""}
            </span>
          )}
        </div>
        <h2>
          <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
            {product.name}
          </Link>
        </h2>
        <div className="jw-product-card__footer">
          <div className="jw-product-card__price">
            <span className={compareAtPrice > price ? "is-sale" : ""}>
              {currencyFormatter.format(price || 0)}
            </span>
            {compareAtPrice > price && (
              <del>{currencyFormatter.format(compareAtPrice)}</del>
            )}
          </div>
          {colors.length > 0 && (
            <div
              className="jw-product-card__swatches"
              aria-label={`${colors.length} colours available`}
            >
              {colors.slice(0, 3).map((color) => (
                <span
                  key={color}
                  title={color}
                  style={{ backgroundColor: colorToCss(color) }}
                />
              ))}
              {colors.length > 3 && <small>+{colors.length - 3}</small>}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function Shop() {
  const [urlParams, setUrlParams] = useSearchParams();
  const cartContext = useContext(CartContext);
  const wishlistItems = cartContext?.wishlistItems || emptyWishlist;
  const toggleWishlist = cartContext?.toggleWishlist || (() => {});

  const [searchTerm, setSearchTerm] = useState(
    () => urlParams.get("search") || urlParams.get("q") || ""
  );
  const [selectedCategories, setSelectedCategories] = useState(() =>
    resolveUrlValues(urlParams.get("category"), categoryOptions)
  );
  const [selectedAudiences, setSelectedAudiences] = useState(() =>
    resolveUrlValues(urlParams.get("audience"), audienceOptions)
  );
  const [selectedActivities, setSelectedActivities] = useState(() =>
    resolveUrlValues(urlParams.get("activity"), activityOptions)
  );
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [priceRange, setPriceRange] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(
    () => urlParams.get("tag") || ""
  );
  const [wishlistOnly, setWishlistOnly] = useState(
    () => urlParams.get("wishlist") === "true"
  );

  const urlSignature = urlParams.toString();

  useEffect(() => {
    const nextParams = new URLSearchParams(urlSignature);
    const nextSearch = nextParams.get("search") || nextParams.get("q") || "";
    const nextCategories = resolveUrlValues(
      nextParams.get("category"),
      categoryOptions
    );
    const nextAudiences = resolveUrlValues(
      nextParams.get("audience"),
      audienceOptions
    );
    const nextActivities = resolveUrlValues(
      nextParams.get("activity"),
      activityOptions
    );
    const nextTag = nextParams.get("tag") || "";
    const nextWishlistOnly = nextParams.get("wishlist") === "true";

    setSearchTerm((current) => (current === nextSearch ? current : nextSearch));
    setSelectedCategories((current) =>
      sameValues(current, nextCategories) ? current : nextCategories
    );
    setSelectedAudiences((current) =>
      sameValues(current, nextAudiences) ? current : nextAudiences
    );
    setSelectedActivities((current) =>
      sameValues(current, nextActivities) ? current : nextActivities
    );
    setSelectedTag((current) => (current === nextTag ? current : nextTag));
    setWishlistOnly((current) =>
      current === nextWishlistOnly ? current : nextWishlistOnly
    );
  }, [urlSignature]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    if (searchTerm.trim()) nextParams.set("search", searchTerm.trim());
    if (selectedCategories.length) {
      nextParams.set("category", selectedCategories.join(","));
    }
    if (selectedAudiences.length) {
      nextParams.set("audience", selectedAudiences.join(","));
    }
    if (selectedActivities.length) {
      nextParams.set("activity", selectedActivities.join(","));
    }
    if (selectedTag) nextParams.set("tag", selectedTag);
    if (wishlistOnly) nextParams.set("wishlist", "true");
    setUrlParams(nextParams, { replace: true });
  }, [
    searchTerm,
    selectedActivities,
    selectedAudiences,
    selectedCategories,
    selectedTag,
    setUrlParams,
    wishlistOnly,
  ]);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") setMobileFiltersOpen(false);
    };
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [mobileFiltersOpen]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedAudiences([]);
    setSelectedActivities([]);
    setSelectedSizes([]);
    setPriceRange("");
    setSelectedTag("");
    setWishlistOnly(false);
  };

  const filteredProducts = useMemo(() => {
    const normalisedSearch = searchTerm.trim().toLowerCase();
    const selectedPrice = priceRanges.find(
      (range) => range.value === priceRange
    );

    const matches = productData.filter((product) => {
      const searchableAttributes = product.attributes
        ? Object.values(product.attributes)
        : [];
      const searchableText = [
        product.name,
        product.category,
        product.audience,
        product.activity,
        product.material,
        ...asArray(product.tags),
        ...asArray(product.description),
        ...searchableAttributes.flatMap(asArray),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const price = Number(product.price);

      return (
        (!normalisedSearch || searchableText.includes(normalisedSearch)) &&
        (!selectedCategories.length ||
          selectedCategories.some((category) =>
            productValues(product, "category").includes(category)
          )) &&
        (!selectedAudiences.length ||
          selectedAudiences.some((audience) =>
            productValues(product, "audience").includes(audience)
          )) &&
        (!selectedActivities.length ||
          selectedActivities.some((activity) =>
            productValues(product, "activity").includes(activity)
          )) &&
        (!selectedSizes.length ||
          selectedSizes.some((size) =>
            productValues(product, "size").includes(size)
          )) &&
        (!selectedPrice ||
          (price >= selectedPrice.min && price < selectedPrice.max)) &&
        (!selectedTag ||
          asArray(product.tags).some(
            (tag) => tag.toLowerCase() === selectedTag.toLowerCase()
          )) &&
        (!wishlistOnly || wishlistItems.includes(product.id))
      );
    });

    return [...matches].sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return Number(a.price) - Number(b.price);
        case "price-high":
          return Number(b.price) - Number(a.price);
        case "rating":
          return Number(b.rating || 0) - Number(a.rating || 0);
        case "newest":
          return Number(b.id) - Number(a.id);
        default:
          return productData.indexOf(a) - productData.indexOf(b);
      }
    });
  }, [
    priceRange,
    searchTerm,
    selectedActivities,
    selectedAudiences,
    selectedCategories,
    selectedSizes,
    selectedTag,
    sortBy,
    wishlistItems,
    wishlistOnly,
  ]);

  const activeFilterCount =
    selectedCategories.length +
    selectedAudiences.length +
    selectedActivities.length +
    selectedSizes.length +
    (priceRange ? 1 : 0) +
    (searchTerm.trim() ? 1 : 0) +
    (selectedTag ? 1 : 0) +
    (wishlistOnly ? 1 : 0);

  const filterPanelProps = {
    selectedCategories,
    setSelectedCategories,
    selectedAudiences,
    setSelectedAudiences,
    selectedActivities,
    setSelectedActivities,
    selectedSizes,
    setSelectedSizes,
    priceRange,
    setPriceRange,
    onClear: clearFilters,
    activeFilterCount,
  };

  return (
    <main className="jw-shop">
      <header className="jw-shop-hero">
        <div className="jw-shop-shell">
          <nav className="jw-shop-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" onClick={scrollToTop}>
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <span>Shop</span>
          </nav>
          <div className="jw-shop-hero__content">
            <div>
              <p className="jw-shop-eyebrow">Explore the collection</p>
              <h1>Outdoor clothing & equipment</h1>
              <p>
                Technical layers, reliable footwear and trail-ready equipment
                for everyday escapes and bigger adventures.
              </p>
            </div>
            <label className="jw-shop-search">
              <span className="sr-only">Search products</span>
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                placeholder="Search jackets, fleece, footwear…"
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm("")}
                >
                  <FiX aria-hidden="true" />
                </button>
              )}
            </label>
          </div>
        </div>
      </header>

      <section className="jw-shop-category-strip" aria-labelledby="shop-by-category-title">
        <div className="jw-shop-shell">
          <h2 id="shop-by-category-title" className="sr-only">
            Shop by category
          </h2>
          <div className="jw-shop-category-strip__scroll">
            <button
              type="button"
              className={selectedCategories.length === 0 ? "is-active" : ""}
              aria-pressed={selectedCategories.length === 0}
              onClick={() => setSelectedCategories([])}
            >
              <span className="jw-shop-category-strip__image">
                {productData[0]?.image && (
                  <img
                    src={productData[0].image}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.hidden = true;
                      event.currentTarget.parentElement?.classList.add(
                        "is-image-missing"
                      );
                    }}
                  />
                )}
              </span>
              <span>All products</span>
            </button>
            {categoryOptions.map((category) => {
              const categoryProduct = productData.find((product) =>
                productValues(product, "category").includes(category)
              );
              return (
                <button
                  type="button"
                  key={category}
                  className={
                    selectedCategories.length === 1 &&
                    selectedCategories.includes(category)
                      ? "is-active"
                      : ""
                  }
                  aria-pressed={
                    selectedCategories.length === 1 &&
                    selectedCategories.includes(category)
                  }
                  onClick={() => setSelectedCategories([category])}
                >
                  <span className="jw-shop-category-strip__image">
                    {categoryProduct?.image && (
                      <img
                        src={categoryProduct.image}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                          event.currentTarget.hidden = true;
                          event.currentTarget.parentElement?.classList.add(
                            "is-image-missing"
                          );
                        }}
                      />
                    )}
                  </span>
                  <span>{category}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="jw-shop-results jw-shop-shell" aria-labelledby="products-title">
        <div className="jw-shop-toolbar">
          <div>
            <h2 id="products-title">
              {wishlistOnly
                ? "Your wishlist"
                : selectedTag
                ? `${selectedTag.charAt(0).toUpperCase()}${selectedTag.slice(1)} products`
                : "All products"}
            </h2>
            <p aria-live="polite">
              {filteredProducts.length} {filteredProducts.length === 1 ? "item" : "items"}
            </p>
          </div>
          <div className="jw-shop-toolbar__actions">
            <button
              type="button"
              className="jw-shop-filter-trigger"
              aria-expanded={mobileFiltersOpen}
              aria-controls="mobile-shop-filters"
              onClick={() => setMobileFiltersOpen(true)}
            >
              <FiSliders aria-hidden="true" />
              Filter {activeFilterCount > 0 && `(${activeFilterCount})`}
            </button>
            <label className="jw-shop-sort">
              <span>Sort by</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest</option>
                <option value="rating">Best rated</option>
                <option value="price-low">Price: low to high</option>
                <option value="price-high">Price: high to low</option>
              </select>
              <FiChevronDown aria-hidden="true" />
            </label>
          </div>
        </div>

        <div className="jw-shop-layout">
          <aside className="jw-shop-sidebar" aria-label="Product filters">
            <FilterPanel idPrefix="desktop" {...filterPanelProps} />
          </aside>

          <div className="jw-shop-products" id="product-results">
            {filteredProducts.length > 0 ? (
              <div className="jw-shop-grid">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isWishlisted={wishlistItems.includes(product.id)}
                    onToggleWishlist={toggleWishlist}
                  />
                ))}
              </div>
            ) : (
              <div className="jw-shop-empty" role="status">
                <span aria-hidden="true">↟</span>
                <h2>No gear found</h2>
                <p>
                  Try a different search or remove a filter to see more of the
                  collection.
                </p>
                <button type="button" onClick={clearFilters}>
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {mobileFiltersOpen && (
        <div className="jw-filter-drawer" id="mobile-shop-filters">
          <button
            className="jw-filter-drawer__backdrop"
            type="button"
            aria-label="Close filters"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div
            className="jw-filter-drawer__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-filter-title"
          >
            <div className="jw-filter-drawer__header">
              <h2 id="mobile-filter-title">Filter products</h2>
              <button
                type="button"
                aria-label="Close filters"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="jw-filter-drawer__body">
              <FilterPanel idPrefix="mobile" {...filterPanelProps} />
            </div>
            <div className="jw-filter-drawer__footer">
              <button type="button" onClick={() => setMobileFiltersOpen(false)}>
                Show {filteredProducts.length}{" "}
                {filteredProducts.length === 1 ? "item" : "items"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Shop;
